import React from "react";
import PropTypes from "prop-types";
import { get } from "lodash";
import { SearchBox, LoadMoreStoriesBase, Link } from "@quintype/components";
import { WebEngage, GA, DeepBI } from "../../../integrations";
import { StoryListBase } from "../tag/index";
import StoryCardML4x3DP1x1 from "../../atoms/story-card-ml4x3-dl1x1";
import { Icon } from "../../atoms/icon";

import styles from "./styles.m.css";
import { FIELDS_FOR_LISTING_PAGE, PAGE_TYPE } from "../../../constants";
import { VikatanCollectionResponsive } from "../../rows/ad-free";

// ─── Sort value mapping ────────────────────────────────────────────────────────
const SORT_PARAM_TO_STATE = {
  score: "relevance",
  "latest-published": "recent"
};
const SORT_STATE_TO_PARAM = {
  relevance: "score",
  recent: "latest-published"
};

// ─── Content-format → story-template mapping ──────────────────────────────────
const FORMAT_TO_TYPE = {
  all: "all",
  news: "text",
  "audio-book": "audio-book",
  video: "video",
  "photo-album": "photo-album"
};

// ─── Analytics ────────────────────────────────────────────────────────────────
function clickEvent(kw) {
  GA.customEvent("Search", "Clicked Search", kw);
  WebEngage.pwEvent("Clicked Search", { q: kw });
  DeepBI.PingSearchClicked({ q: kw });
}

// ─── Search form render prop ──────────────────────────────────────────────────
function createDrawForm(urlParams) {
  function DrawForm({ children }) {
    const searchParam = children.props.value || "";

    const handleSearch = () => {
      if (!searchParam.trim()) return;
      clickEvent(searchParam);

      const newParams = new URLSearchParams();
      newParams.set("q", searchParam);

      if (urlParams.sort) newParams.set("sort", urlParams.sort);
      if (urlParams.type) newParams.set("type", urlParams.type);
      if (urlParams["content-types"]) newParams.set("content-types", urlParams["content-types"]);
      if (urlParams["search-type"]) newParams.set("search-type", urlParams["search-type"]);

      window.location.href = `/search?${newParams.toString()}`;
    };

    return (
      <React.Fragment>
        <label htmlFor="stg" className={styles["search-query"]} key={"label1"}>
          {children}
        </label>
        <button
          title="search"
          aria-label="search"
          onClick={handleSearch}
          role="button"
          className={styles["search-btn"]}
          type="submit"
          key={"btn1"}
        >
          <Icon type="search" key={"search1"} />
        </button>
      </React.Fragment>
    );
  }

  DrawForm.propTypes = {
    children: PropTypes.element
  };

  return DrawForm;
}

// ─── Quintype story list — used inside LoadMoreStoriesBase ────────────────────
const SearchStoryList = ({ stories, loading, onLoadMore, noMoreStories }) => (
  <React.Fragment>
    {stories.length > 0 ? (
      <React.Fragment>
        {stories.map(story => (
          <div key={story.id || story.slug} className={styles["search-story-card"]}>
            <StoryCardML4x3DP1x1 story={story} SearchCard={true} />
          </div>
        ))}
      </React.Fragment>
    ) : (
      <p className={styles["no-results"]}>No results found</p>
    )}

    {!noMoreStories &&
      stories.length >= 9 && (
        <div className={styles["load-more-wrap"]}>
          <button disabled={loading} onClick={onLoadMore} className={styles["load-more-btn"]}>
            {loading ? "LOADING..." : "மேலும் படிக்க"}
          </button>
        </div>
      )}
  </React.Fragment>
);

SearchStoryList.propTypes = {
  stories: PropTypes.array,
  loading: PropTypes.bool,
  onLoadMore: PropTypes.func,
  noMoreStories: PropTypes.bool
};

// ─── Audio books section ──────────────────────────────────────────────────────
const AudioBooksSection = ({ stories: initialStories, audioTotal, audioFetched, q, ssrLimit = 250 }) => {
  const [stories, setStories] = React.useState(initialStories || []);
  const [loading, setLoading] = React.useState(false);
  const [offset, setOffset] = React.useState(ssrLimit);
  const [noMoreStories, setNoMoreStories] = React.useState(
    audioFetched < ssrLimit || (initialStories || []).length >= (audioTotal || 0)
  );

  const loadMore = () => {
    setLoading(true);
    global
      .fetch(`/v1/api/audio-books/search?q=${encodeURIComponent(q)}&offset=${offset}&limit=50`)
      .then(res => res.json())
      .then(data => {
        const newItems = get(data, ["audioSearchList", "items"], []);
        setStories(prev => [...prev, ...newItems]);
        setOffset(prev => prev + 50);
        setLoading(false);
        setNoMoreStories(newItems.length < 50);
      })
      .catch(err => {
        console.error("Audio load more error:", err);
        setLoading(false);
      });
  };

  if (!stories || stories.length === 0) return null;

  return (
    <div className={styles["audio-section"]}>
      <h3 className={styles["audio-section-title"]}>Audio Books</h3>
      <div className={styles["audio-results"]}>
        {stories.map(story => (
          <div key={story.id || story.slug} className={styles["search-story-card"]}>
            <a href={story.customLink} target="_blank" rel="noopener noreferrer" className={styles["audio-book-link"]}>
              <StoryCardML4x3DP1x1 story={story} SearchCard={true} />
            </a>
          </div>
        ))}
      </div>

      {!noMoreStories && (
        <div className={styles["load-more-wrap"]}>
          <button disabled={loading} onClick={loadMore} className={styles["load-more-btn"]}>
            {loading ? "LOADING..." : "மேலும் படிக்க"}
          </button>
        </div>
      )}
    </div>
  );
};

AudioBooksSection.propTypes = {
  stories: PropTypes.array,
  audioTotal: PropTypes.number,
  audioFetched: PropTypes.number,
  ssrLimit: PropTypes.number,
  q: PropTypes.string
};

// ─── Search Landing Page — sections only, no search box ──────────────────────
// Search box is rendered by SearchPage.render() and shared between
// landing page and search results page.
const SearchLandingPage = ({ data }) => {
  const { trending, audioWidgets, magazineWidgets, gameWidgets, bottomLinks } = data;

  const trendingStories = get(trending, ["items"], [])
    .map(item => get(item, ["story"], null))
    .filter(Boolean)
    .slice(0, 6);

  return (
    <React.Fragment>
      {/* ── Trending Stories ── */}
      {trendingStories.length > 0 && (
        <div className={styles["landing-section"]}>
          <h2 className={styles["landing-section-title"]}>டிரெண்டிங் செய்திகள்</h2>
          <div className={styles["trending-grid"]}>
            {trendingStories.map(story => (
              <div key={story.id || story.slug} className={styles["trending-card"]}>
                <StoryCardML4x3DP1x1 story={story} landingSearchCard={true} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Latest Magazines ── */}
      {magazineWidgets &&
        magazineWidgets.length > 0 && (
          <div className={styles["landing-section"]}>
            <h2 className={styles["landing-section-title"]}>லேட்டஸ்ட் இதழ்கள்</h2>
            <div className={styles["landing-links"]}>
              {magazineWidgets.map((item, index) => (
                <React.Fragment key={item.title}>
                  <a href={item.url} className={styles["landing-link"]} target="_blank" rel="noopener noreferrer">
                    {item.title}
                  </a>
                  {index < magazineWidgets.length - 1 && <span className={styles["landing-link-sep"]}>|</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

      {/* ── Audio Books ── */}
      {audioWidgets &&
        audioWidgets.length > 0 && (
          <div className={styles["landing-section"]}>
            <h2 className={styles["landing-section-title"]}>ஆடியோ புத்தகங்கள்</h2>
            <div className={styles["landing-links"]}>
              {audioWidgets.map((item, index) => (
                <React.Fragment key={item.title}>
                  <a href={item.slug} className={styles["landing-link"]} target="_blank" rel="noopener noreferrer">
                    {item.title}
                  </a>
                  {index < audioWidgets.length - 1 && <span className={styles["landing-link-sep"]}>|</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

      {/* ── Vikatan Games ── */}
      {gameWidgets &&
        gameWidgets.length > 0 && (
          <div className={styles["landing-section"]}>
            <h2 className={styles["landing-section-title"]}>விகடன் கேம்ஸ்</h2>
            <div className={styles["landing-links"]}>
              {gameWidgets.map((item, index) => (
                <React.Fragment key={item.title}>
                  <a
                    href={item.redirectUrl}
                    className={styles["landing-link"]}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.title}
                  </a>
                  {index < gameWidgets.length - 1 && <span className={styles["landing-link-sep"]}>|</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

      {/* ── Bottom Buttons ── */}
      {bottomLinks && (
        <div className={styles["landing-bottom-btns"]}>
          <a
            href={bottomLinks.videos}
            className={styles["landing-bottom-btn"]}
            target="_blank"
            rel="noopener noreferrer"
          >
            லேட்டஸ்ட் வீடியோஸ்
          </a>
          <a
            href={bottomLinks.magazines}
            className={styles["landing-bottom-btn"]}
            target="_blank"
            rel="noopener noreferrer"
          >
            விகடன் பிரசுரம் புத்தகங்கள்
          </a>
        </div>
      )}
    </React.Fragment>
  );
};

SearchLandingPage.propTypes = {
  data: PropTypes.shape({
    trending: PropTypes.object,
    audioWidgets: PropTypes.array,
    magazineWidgets: PropTypes.array,
    gameWidgets: PropTypes.array,
    bottomLinks: PropTypes.shape({
      videos: PropTypes.string,
      magazines: PropTypes.string
    })
  })
};

// ─── Main SearchPage ──────────────────────────────────────────────────────────
class SearchPage extends React.Component {
  constructor(props) {
    super(props);

    const params = props.data.params || {};

    const initialSort = SORT_PARAM_TO_STATE[params.sort] || "relevance";
    const TYPE_TO_FORMAT = {
      text: "news",
      video: "video",
      "audio-book": "audio-book",
      "photo-album": "photo-album"
    };

    const typeFromUrl = params.type;
    const ALL_FORMATS = ["all", "news", "audio-book", "video", "photo-album"];
    const initialFormats =
      typeFromUrl && typeFromUrl !== "all"
        ? typeFromUrl.split(",").map(t => TYPE_TO_FORMAT[t.trim()] || t.trim())
        : ALL_FORMATS;

    this.state = {
      showFilterPopup: false,
      committed: {
        selectedFormats: initialFormats,
        selectedSort: initialSort,
        selectedSearchType: params["search-type"] || "any-word"
      },
      draft: {
        selectedFormats: initialFormats,
        selectedSort: initialSort,
        selectedSearchType: params["search-type"] || "any-word"
      }
    };

    this.handleFilterIconClick = this.handleFilterIconClick.bind(this);
    this.closeFilterPopup = this.closeFilterPopup.bind(this);
    this.applyFilter = this.applyFilter.bind(this);
    this.toggleFormat = this.toggleFormat.bind(this);
  }

  componentDidMount() {
    WebEngage.registerPageView();
    GA.registerPageView(PAGE_TYPE.SEARCH_PAGE);
    DeepBI.registerPageView({ collection: this.props });
  }

  handleFilterIconClick() {
    this.setState(prev => ({
      showFilterPopup: true,
      draft: { ...prev.committed }
    }));
  }

  closeFilterPopup() {
    this.setState(prev => ({
      showFilterPopup: false,
      draft: { ...prev.committed }
    }));
  }

  toggleFormat(format) {
    this.setState(prev => {
      const ALL_SPECIFIC = ["news", "audio-book", "video", "photo-album"];
      const current = prev.draft.selectedFormats;
      let next;

      if (format === "all") {
        const allChecked = current.includes("all") && ALL_SPECIFIC.every(f => current.includes(f));
        next = allChecked ? [] : ["all", ...ALL_SPECIFIC];
      } else {
        const exists = current.includes(format);
        const base = current.filter(f => f !== "all" && f !== format);

        if (exists) {
          next = base;
        } else {
          const updated = [...base, format];
          const allSpecificNow = ALL_SPECIFIC.every(f => updated.includes(f));
          next = allSpecificNow ? ["all", ...updated] : updated;
        }
      }

      return { draft: { ...prev.draft, selectedFormats: next } };
    });
  }

  applyFilter() {
    const { selectedFormats, selectedSort, selectedSearchType } = this.state.draft;

    const baseParams = { ...this.props.data.params };
    const searchInput = document.getElementById("stg");
    const inputValue = searchInput && searchInput.value.trim();

    if (inputValue) {
      baseParams.q = inputValue; // new query typed
    }
    // if input cleared — keep existing URL query (don't delete)

    const urlHasQuery = !!(this.props.data.query && this.props.data.query.trim());

    if (!inputValue && !urlHasQuery) {
      // No input + no URL query → close popup + focus input
      this.setState({ showFilterPopup: false }, () => {
        const searchInput = document.getElementById("stg");
        if (searchInput) searchInput.focus();
      });
      return;
    }
    baseParams.sort = SORT_STATE_TO_PARAM[selectedSort] || "score";

    const ALL_SPECIFIC = ["news", "audio-book", "video", "photo-album"];
    const hasAllSentinel = selectedFormats.includes("all");
    const allSpecificNow = ALL_SPECIFIC.every(f => selectedFormats.includes(f));
    const specificSelected = selectedFormats.filter(f => f !== "all" && f.trim() !== "");

    if (hasAllSentinel || allSpecificNow || specificSelected.length === 0) {
      baseParams.type = "all";
    } else {
      baseParams.type = specificSelected.map(f => FORMAT_TO_TYPE[f]).join(",");
    }

    baseParams["search-type"] = selectedSearchType;
    baseParams["content-types"] = "story";

    this.setState({ committed: { selectedFormats, selectedSort, selectedSearchType } });

    const URL_PARAMS = ["q", "sort", "type", "content-types", "search-type"];
    const queryString = URL_PARAMS.filter(k => baseParams[k] !== undefined && baseParams[k] !== "")
      .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(baseParams[k])}`)
      .join("&");

    window.location.href = `/search?${queryString}`;
  }

  renderFilterPopup() {
    if (!this.state.showFilterPopup) return null;

    const formats = [
      { key: "all", label: "All" },
      { key: "news", label: "News" },
      { key: "audio-book", label: "Audio Book" },
      { key: "video", label: "Video" },
      { key: "photo-album", label: "Album" }
    ];

    return (
      <div className={styles["filter-popup-overlay"]} onClick={this.closeFilterPopup}>
        <div className={styles["filter-popup"]} onClick={e => e.stopPropagation()}>
          <button className={styles["filter-popup-close"]} onClick={this.closeFilterPopup}>
            ✕
          </button>
          <h2 className={styles["filter-popup-title"]}>Filters</h2>
          <p className={styles["filter-popup-subtitle"]}>உங்கள் விருப்ப தேர்வை செய்து மாற்றி கொள்ளுங்கள்</p>

          <div className={styles["filter-section"]}>
            <h3 className={styles["filter-section-title"]}>Select Content Format</h3>
            <div className={styles["filter-checkboxes"]}>
              {formats.map(({ key, label }) => (
                <label key={key} className={styles["filter-checkbox-label"]}>
                  <input
                    type="checkbox"
                    className={styles["filter-checkbox"]}
                    checked={this.state.draft.selectedFormats.includes(key)}
                    onChange={() => this.toggleFormat(key)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className={styles["filter-divider"]} />

          <div className={styles["filter-row"]}>
            <div className={styles["filter-section"]}>
              <h3 className={styles["filter-section-title"]}>Sort By</h3>
              <div className={styles["filter-radios"]}>
                {[{ key: "relevance", label: "Relevance" }, { key: "recent", label: "Recent" }].map(
                  ({ key, label }) => (
                    <label key={key} className={styles["filter-radio-label"]}>
                      <input
                        type="radio"
                        className={styles["filter-radio"]}
                        name="sort"
                        value={key}
                        checked={this.state.draft.selectedSort === key}
                        onChange={() => this.setState(prev => ({ draft: { ...prev.draft, selectedSort: key } }))}
                      />
                      <span>{label}</span>
                    </label>
                  )
                )}
              </div>
            </div>

            <div className={styles["filter-section"]}>
              <h3 className={styles["filter-section-title"]}>Search Type</h3>
              <div className={styles["filter-radios"]}>
                {[{ key: "any-word", label: "Any Word" }, { key: "exact-phrase", label: "Exact Phrase" }].map(
                  ({ key, label }) => (
                    <label key={key} className={styles["filter-radio-label"]}>
                      <input
                        type="radio"
                        className={styles["filter-radio"]}
                        name="search-type"
                        value={key}
                        checked={this.state.draft.selectedSearchType === key}
                        onChange={() => this.setState(prev => ({ draft: { ...prev.draft, selectedSearchType: key } }))}
                      />
                      <span>{label}</span>
                    </label>
                  )
                )}
              </div>
            </div>
          </div>

          <div className={styles["filter-divider"]} />

          <button className={styles["filter-apply-btn"]} onClick={this.applyFilter}>
            Apply Filter
          </button>
        </div>
      </div>
    );
  }

  getPath(key, value) {
    const newParams = { ...this.props.data.params, [key]: value };
    return (
      "/search?" +
      Object.keys(newParams)
        .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(newParams[k])}`)
        .join("&")
    );
  }

  renderSortOptions() {
    return (
      <div className={styles["sort-options"]}>
        <span className={styles["sort-label"]}>Sort By:</span>
        <Link
          className={`${styles["sort-link"]} ${
            this.props.data.params["sort"] === "latest-published" ? styles["selected"] : ""
          }`}
          href={this.getPath("sort", "latest-published")}
        >
          Recent
        </Link>
        <Link
          className={`${styles["sort-link"]} ${this.props.data.params["sort"] === "score" ? styles["selected"] : ""}`}
          href={this.getPath("sort", "score")}
        >
          Relevance
        </Link>
      </div>
    );
  }

  renderFilterOptions() {
    return (
      <div className={styles["filter-options"]}>
        <Link
          className={`${styles["filter-link"]} ${
            !this.props.data.params["type"] || this.props.data.params["type"] === "all" ? styles["selected"] : ""
          }`}
          href={this.getPath("type", "all")}
        >
          All ({this.props.data.total || 0})
        </Link>
        <Link
          className={`${styles["filter-link"]} ${this.props.data.params["type"] === "text" ? styles["selected"] : ""}`}
          href={this.getPath("type", "text")}
        >
          News ({get(this.props.data, ["facets", "text"]) || 0})
        </Link>
        <Link
          className={`${styles["filter-link"]} ${
            this.props.data.params["type"] === "photo-album" ? styles["selected"] : ""
          }`}
          href={this.getPath("type", "photo-album")}
        >
          Album ({get(this.props.data, ["facets", "photo-album"]) || 0})
        </Link>
        <Link
          className={`${styles["filter-link"]} ${this.props.data.params["type"] === "video" ? styles["selected"] : ""}`}
          href={this.getPath("type", "video")}
        >
          Video ({get(this.props.data, ["facets", "video"]) || 0})
        </Link>
        <Link
          className={`${styles["filter-link"]} ${
            this.props.data.params["type"] === "audio-book" ? styles["selected"] : ""
          }`}
          href={this.getPath("type", "audio-book")}
        >
          Audio Book ({get(this.props.data, ["facets", "audio-book"]) || 0})
        </Link>
      </div>
    );
  }

  // ── Search results — rendered when query is present ───────────────────────
  renderSearchResults() {
    const urlParams = this.props.data.params || {};
    const allStories = this.props.data.stories || [];
    const quintypeStories = allStories.filter(s => !s.isAudioBook);
    const audioStories = allStories.filter(s => s.isAudioBook);
    const isAudioOnly = urlParams.type === "audio-book";
    const hasNoResults = quintypeStories.length === 0 && audioStories.length === 0;

    const quintypeParams = { ...urlParams };
    if (!quintypeParams.type || quintypeParams.type === "all" || quintypeParams.type === "audio-book") {
      delete quintypeParams.type;
    }
    delete quintypeParams["search-type"];
    quintypeParams["content-types"] = "story";

    return (
      <React.Fragment>
        {/* ── Sort / facet bar ── */}
        <div className={styles["sort-facets"]}>
          <div className={`${styles["sort-facets-desktop"]}`}>
            {this.renderFilterOptions()}
            {this.renderSortOptions()}
          </div>
        </div>

        {/* ── No results ── */}
        {hasNoResults && (
          <p className={styles["no-results"]}>
            No results found for &quot;
            {this.props.data.query}
            &quot;
          </p>
        )}

        {/* ── Quintype stories ── */}
        {!isAudioOnly &&
          quintypeStories.length > 0 && (
            <div className={styles["search-results-card"]}>
              <LoadMoreStoriesBase
                api="/api/v1/advanced-search"
                apiResponseTransformer={result => result.items}
                template={SearchStoryList}
                numStoriesToLoad={9}
                data={{ ...this.props.data, stories: quintypeStories }}
                fields={FIELDS_FOR_LISTING_PAGE}
                params={quintypeParams}
              />
            </div>
          )}

        {/* ── Audio books ── */}
        <AudioBooksSection
          stories={audioStories}
          audioTotal={this.props.data.audioTotal}
          audioFetched={this.props.data.audioFetched}
          q={this.props.data.query}
          ssrLimit={250}
        />

        {/* ── Bottom ad ── */}
        <div className={styles["search-ad"]}>
          <VikatanCollectionResponsive
            DesktopAdtype="Vikatan2023_Desktop_SP_BTF_728x90"
            MobileAdtype="Vikatan2023_Mobile_SP_TOP_320x100"
            placement="bundle"
          />
        </div>
      </React.Fragment>
    );
  }

  render() {
    const urlParams = this.props.data.params || {};
    const DrawForm = createDrawForm(urlParams);
    const displayQuery = (this.props.data.query || "").replace(/(<([^>]+)>)/gi, "").replace(/[^a-zA-Z ]/g, "");
    const hasQuery = !!(this.props.data.query && this.props.data.query.trim());

    return (
      <div className={`main-page ${styles["base-container"]}`}>
        <div />
        <div className={styles.base}>
          <div className="container">
            <h1 className="sr-only">Search</h1>

            {/* ── Search box + filter icon — always visible on both landing and results ── */}
            <div className={styles["search-header"]}>
              <div className={styles["search-input-wrap"]}>
                <SearchBox
                  template={DrawForm}
                  placeholder={"Search"}
                  initValue={displayQuery}
                  inputId="stg"
                  onSubmitHandler={query => {
                    if (!query || !query.trim()) {
                      window.location.href = "/search";
                    }
                  }}
                />
              </div>
              <div className={styles["filter-icon-wrap"]}>
                <button
                  className={styles["filter-icon-btn"]}
                  onClick={this.handleFilterIconClick}
                  aria-label="Open filters"
                  disabled={!hasQuery}
                >
                  <Icon type="icon-filter" className={styles["filter-icon"]} />
                </button>
              </div>
            </div>

            {/* ── Filter popup — always available ── */}
            {this.renderFilterPopup()}

            {/* ── Landing page sections OR search results ── */}
            {this.props.data.isLandingPage ? <SearchLandingPage data={this.props.data} /> : this.renderSearchResults()}
          </div>
        </div>
        <div />
      </div>
    );
  }
}

export { SearchPage };

SearchPage.propTypes = {
  data: PropTypes.shape({
    stories: StoryListBase.propTypes.stories,
    isLandingPage: PropTypes.bool,
    total: PropTypes.number,
    query: PropTypes.string,
    audioTotal: PropTypes.number,
    audioFetched: PropTypes.number,
    params: PropTypes.shape({
      q: PropTypes.string,
      type: PropTypes.string,
      sort: PropTypes.string,
      "search-type": PropTypes.string
    }),
    facets: PropTypes.shape({
      "photo-album": PropTypes.number,
      text: PropTypes.number,
      video: PropTypes.number,
      "audio-book": PropTypes.number
    }),
    trending: PropTypes.object,
    audioWidgets: PropTypes.array,
    magazineWidgets: PropTypes.array,
    gameWidgets: PropTypes.array,
    bottomLinks: PropTypes.shape({
      videos: PropTypes.string,
      magazines: PropTypes.string
    })
  })
};
